/**
 * GET /v1/firm/book-summary — book-wide FICA + beneficial-ownership aggregates.
 *
 * Under first-class tenancy a firm's trusts live in its CLIENTS' tenants, so
 * `useTrusts()` and `useBoLodgementAttempts()` (both scoped to the firm's own
 * tenant) are empty for every real firm. The dashboard handled that honestly
 * rather than showing a false all-clear: it detected the contradiction and
 * replaced its "0" claims with "tracked per client" pointers. That left a firm
 * with 150 clients looking at three cards saying nothing was aggregated. This
 * is the read those cards were missing.
 *
 * Mirrors the `firm/document-coverage` module: fail fast when the endpoint is
 * absent or the caller is not a tenant-wide reader, and export the query key +
 * fetcher so the admin prefetch fills the SAME cache entry this hook reads.
 */

import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from './client';

export interface BookTrustRow {
  trustId: string;
  name: string | null;
  ficaDaysOut: number;
  nextFicaReview: string | null;
  lastUpdated: string | null;
  registration: string | null;
  clientTenantId: string;
  clientDisplayName: string;
  /** TRS-03: `unverified` = people on the register and none verified; such a
   *  row carries no review date, because there is no review cycle yet. */
  ficaBucket: 'unverified' | 'overdue' | 'due' | 'current';
  /** TRS-03: the initial-FICA fact (`false` = none exists, `null` = unknown).
   *  Absent from an older backend. */
  initialFicaOnFile?: boolean | null;
}

export interface BookBoRow {
  id: string;
  trustId: string;
  /** Null when the owning trust could not be read (a grant revoked mid-read).
   *  Not a placeholder string — the UI decides how to render an absent name. */
  trustName: string | null;
  status: string;
  statusChangedAt: string;
  snapshotIds: string[];
  clientTenantId: string;
  clientDisplayName: string;
}

export interface FirmBookSummaryResponse {
  fica: {
    totalTrusts: number;
    overdue: number;
    dueIn30: number;
    current: number;
    /** TRS-03: trusts with people recorded and no initial FICA; in none of the
     *  three periodic buckets. Absent from an older backend. */
    unverified?: number;
    /** Overdue first, then soonest-due. Healthy trusts are excluded — this is a
     *  worklist, not a directory. Capped server-side. */
    attention: BookTrustRow[];
  };
  beneficialOwners: {
    /** The whole book's in-flight count, not the length of `items`. */
    pendingCount: number;
    items: BookBoRow[];
  };
  totalClients: number;
  shownClients: number;
  /** DSH-04 (Beno): assets and, separately, properties recorded across every
   *  granted client tenant - counted in the one book-wide aggregate. Absent
   *  from an older backend. */
  assets?: { total: number; properties: number };
  /** True when the book exceeded the server's fan-out cap. The UI should say
   *  so rather than present a partial book as complete. */
  truncated: boolean;
  correlationId: string;
}

function failFastOnMissing(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && (error.status === 404 || error.status === 403)) {
    return false;
  }
  return failureCount < 3;
}

/** Exported so the admin-dashboard prefetch fills the SAME cache entry this
 *  hook reads. Duplicating the literal is how a prefetch silently stops working. */
export const FIRM_BOOK_SUMMARY_QUERY_KEY = ['firm', 'book-summary'] as const;

export function fetchFirmBookSummary(signal?: AbortSignal) {
  return api<FirmBookSummaryResponse>('/api/v1/firm/book-summary', { signal });
}

export function useFirmBookSummary(opts: { enabled?: boolean } = {}) {
  return useQuery<FirmBookSummaryResponse>({
    queryKey: FIRM_BOOK_SUMMARY_QUERY_KEY,
    queryFn: ({ signal }) => fetchFirmBookSummary(signal),
    enabled: opts.enabled ?? true,
    retry: failFastOnMissing,
  });
}
